import React, { useEffect, useState } from 'react';
import DownloadingOutlinedIcon from '@mui/icons-material/DownloadingOutlined';
import {
  OutlinedInput,
  MenuItem,
  Select,
  Stack,
  Checkbox,
  ListItemText,
  Button,
  Typography,
  Tooltip,
  Fade,
  Alert,
} from '@mui/material';
import styled from 'styled-components';
import { ColorsV2 } from 'app/constants/color';
import { Buttons } from 'app/constants/sizing';
import apiService from 'app/services/apiService';

const StyledInput = styled(OutlinedInput)({
  backgroundColor: ColorsV2.primary,
  height: '40px',
  width: '265px',
  border: 'solid 2px transparent',
  borderRadius: '4px',

  '& .MuiInputBase-input': {
    color: ColorsV2.white,
  },

  '&:hover': {
    border: 'solid 2px transparent',
    borderColor: ColorsV2.acent,
  },
});

const ViolationExportMenu = ({ policyData = {}, dataSources = [], setToast, t }) => {

  const MAX_REPORT_NAME_CC = 50; // Max character count for report name

  const [informationClasses, setInformationClasses] = useState([]);
  const [selectedDataSources, setSelectedDataSources] = useState([]);
  const [selectedInformationClasses, setSelectedInformationClasses] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [selectedRestrictions, setSelectedRestrictions] = useState([]);
  const [informationTypes, setInformationTypes] = useState([]);
  const [selectedInformationTypes, setSelectedInformationTypes] = useState([]);
  const [reportName, setReportName] = useState('');
  const [reportNameCC, setReportNameCC] = useState(0);
  const [reportNameCCAlert, setReportNameCCAlert] = useState(false);
  const [estimatedDeviations, setEstimatedDeviations] = useState(0);
  const [informationClassOpen, setInformationClassOpen] = useState(false);
  const [informationClassHover, setInformationClassHover] = useState(false);

  const menuProps = {
    sx: {
      maxHeight: '450px',
      '& .MuiPaper-root': {
        backgroundColor: ColorsV2.primary,
        '& .MuiMenuItem-root': {
          color: ColorsV2.white,
        },
      },
    },
  };

  let res = null;

  const generateReport = async () => {
    const exportData = {
      name: reportName,
      datasources: selectedDataSources.map(dataSource => ({
        id: dataSource.id,
        name: dataSource.displayName,
      })),
      classLevels: selectedInformationClasses.map(informationClass => ({
        id: informationClass.id,
        name: informationClass.displayName,
      })),
      infoTypes: selectedInformationTypes.map(informationType => ({
        id: informationType.id,
        name: informationType.displayName,
      })),
      restrictions: selectedRestrictions.map(restriction => ({
        id: restriction.id,
        name: restriction.displayName,
      })),
    };

    try {
      res = await apiService.fetchExportDeviationsReportRequest(exportData);

      if(res.data.status === 'success') {
        setToast({
          open: true,
          message: t('export_violation_request_notification'),
          severity: 'success',
        });
        setSelectedDataSources([]); // Reset selection on successful report request
        setSelectedInformationClasses([]);
        setSelectedInformationTypes([]);
        setSelectedRestrictions([]);
      };
    } catch (error) {
      console.error('Error fetching export deviations report:', error);
      setToast({
        open: true,
        message: t('export_violation_request_fail_notification'),
        severity: 'error',
      });
    }
  };

  const fetchEstimatedDeviations = async () => {
    try {
      res = await apiService.fetchEstimatedDeviationsReportSize({
        datasources: selectedDataSources.map(dataSource => dataSource.id),
        classLevels: selectedInformationClasses.map(informationClass => informationClass.id),
        infoTypes: selectedInformationTypes.map(informationType => informationType.id),
        restrictions: selectedRestrictions.map(restriction => restriction.id),
      });
    } catch (error) {
      console.error('Error fetching policy by ID:', error);
    } finally {
      setEstimatedDeviations(res.data.hint);
    }
  };

  const removeDuplicates = unfilteredArray => {
    if (!unfilteredArray?.map) {
      return [];
    }
    const duplicates = new Set();
    const filteredArray = unfilteredArray.filter(item => {
      if (duplicates.has(item.id)) {
        return false;
      }
      duplicates.add(item.id);
      return true;
    });
    return filteredArray;
  };

  // Check if the current selection meets the criteria for a valid selection (In regards to creating an estimated number of deviations)
  const isValidSelection = () => {
    return (
      selectedDataSources.length <= 0 ||
      selectedInformationClasses.length <= 0 ||
      selectedInformationTypes.length <= 0 ||
      selectedRestrictions.length <= 0
    );
  };

  // Set information Classes from policyData
  useEffect(() => {
    // Check for levels property (contains information classes) and make sure it's an array with entries
    if (policyData?.levels?.map && policyData.levels.length > 0) {
      setInformationClasses(policyData.levels);
    }
  }, [policyData]);

  useEffect(() => {
    const newRestrictions = [];
    const newInformationTypes = [];
    // Set restrictions and information types from policyData based on selectedInformationClasses

    if (policyData.levels?.map) {
      policyData.levels.map(informationClass => {
        if (
          !selectedInformationClasses.some(
            selectedClass => selectedClass.id === informationClass.id
          )
        )
          return; // Filter out any results not part of the selected information classes
        if (informationClass?.protectionAspects?.map) {
          informationClass.protectionAspects.map(protectionAspect => {
            protectionAspect.restrictions?.map(restriction => {
              newRestrictions.push(restriction);
            });
          });
        }
        if (informationClass?.infoObjects?.map) {
          informationClass.infoObjects.map(informationType => {
            newInformationTypes.push(informationType);
          });
        }
      });
    }
    setRestrictions(removeDuplicates(newRestrictions));
    setInformationTypes(removeDuplicates(newInformationTypes));
  }, [selectedInformationClasses, policyData]);

  useEffect(() => {
    // Fetch estimated deviations for selection
    if (!fetchEstimatedDeviations || isValidSelection()) return; // Only send a request to the backend for estimated deviations if we have made valid selection
    fetchEstimatedDeviations();
  }, [
    selectedDataSources,
    selectedInformationClasses,
    selectedInformationTypes,
    selectedRestrictions,
  ]);

  const handleReportName = event => {
    if (!event?.target) return;
    const newName = event.target.value;
    if (newName?.length > MAX_REPORT_NAME_CC){
      setReportNameCCAlert(true);
      setTimeout(() => {
        setReportNameCCAlert(false);
      }, 3000);
    } else {
      setReportName(newName);
      setReportNameCC(newName?.length);
    }
  };

  const handleChange = event => {
    const { name, value } = event.target;
    const newItem = value[value.length - 1] || { id: '' };

    if (newItem === 'all') {
      let isDeselect = false;
      switch (
        name // If all items are already selected, deselect all - Otherwise select all elements
      ) {
        case 'data-sources':
          setSelectedDataSources(prev =>
            prev.length === dataSources.length
              ? []
              : dataSources.map(item => ({ id: item.id, displayName: item.name }))
          );
          break;
        case 'information-classes':
          setSelectedInformationClasses(prev => {
            if (prev.length === informationClasses.length) {
              isDeselect = true;
              return [];
            } else {
              return informationClasses.map(item => ({ id: item.id, displayName: item.name }));
            }
          });
          if (isDeselect) {
            // Clear selected information types and restrictions to prevent any selected values from being rendered inaccessible after an information class is deselected
            setSelectedInformationTypes([]);
            setSelectedRestrictions([]);
          }
          break;
        case 'information-types':
          setSelectedInformationTypes(prev =>
            prev.length === informationTypes.length
              ? []
              : informationTypes.map(item => ({ id: item.id, displayName: item.name }))
          );
          break;
        case 'restrictions':
          setSelectedRestrictions(prev =>
            prev.length === restrictions.length
              ? []
              : restrictions.map(item => ({ id: item.id, displayName: item.name }))
          );
          break;
        default:
          break;
      }
      return;
    }

    const handleReselect = prev => {
      if (prev.some(item => item.id === newItem.id)) {
        // Check if the most recently added item was already selected
        return { value: prev.filter(item => item.id !== newItem.id), isDeselect: true }; // If so: deselect it
      }
      return { value, isDeselect: false };
    };
    let newValue = {};
    switch (name) {
      case 'data-sources':
        setSelectedDataSources(prev => handleReselect(prev).value);
        break;
      case 'information-classes':
        setSelectedInformationClasses(prev => {
          newValue = handleReselect(prev);
          return newValue.value;
        });
        if (newValue.isDeselect) {
          setSelectedInformationTypes([]); // Clear to prevent any selected values from being rendered inaccessible after an information class is deselected
          setSelectedRestrictions([]);
        }
        break;
      case 'information-types':
        setSelectedInformationTypes(prev => handleReselect(prev).value);
        break;
      case 'restrictions':
        setSelectedRestrictions(prev => handleReselect(prev).value);
        break;
      default:
        break;
    }
  };

  return (
    <Stack direction='column' sx={{ width: '95%', maxWidth: '1150px', backgroundColor: ColorsV2.secondary, borderRadius: '4px', padding: '20px'}}>
      <Stack pb={4}>
        <Typography variant='h5' sx={{ color: ColorsV2.white }}>
          {t('export_heading')}
        </Typography>

        <Typography variant='subtitle1' sx={{ color: ColorsV2.textInfo }}>
          {t('export_description')}
        </Typography>
      </Stack>
      <Stack direction='column' sx={{ marginY: '10px' }}>
        <Stack direction='row' alignItems='center' gap='10px'>
          <StyledInput
            value={reportName}
            onChange={handleReportName}
            placeholder={t('export_input')}
            size='large'
            inputProps={{
              sx: {
                fontSize: '1rem',
                color: ColorsV2.white,
                borderColor: ColorsV2.acent,
                borderWidth: '2px',
                outline: 'none',
              },
            }}
            sx={{
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                // Disable default focus styling
                borderColor: 'transparent',
              },
              '&.MuiOutlinedInput-root.Mui-focused': {
                boxShadow: 'none',
              },
            }}
          />
          <Fade in={reportNameCCAlert}>
            <Alert
              severity='error'
              sx={{
                color: ColorsV2.white,
                backgroundColor: ColorsV2.primary,
                height: '40px',
                paddingY: '0px',
              }}
            >
              {t('export_text_field_restriction')}
            </Alert>
          </Fade>
        </Stack>
        <Typography variant='body2' sx={{ color: ColorsV2.textInfo, marginLeft: '10px' }}>
          {reportNameCC} / {MAX_REPORT_NAME_CC}
        </Typography>
      </Stack>
      <Stack direction='row' flexWrap='wrap' gap='10px' sx={{ maxWidth: '1110px'}}>
        {dataSources.length > 0 ? (
          <Select
            multiple
            displayEmpty
            name='data-sources'
            onChange={handleChange}
            value={selectedDataSources}
            input={<StyledInput />}
            MenuProps={menuProps}
            sx={{
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                // Disable default focus styling
                borderColor: 'transparent',
              },
              '&.MuiOutlinedInput-root .Mui-focused': {
                boxShadow: 'none',
              },
            }}
            renderValue={selected => {
              if (selected.length === 0) {
                return <em>{t('export_select_data_sources')}</em>; // Placeholder text
              }
              if (selected.length === dataSources.length) {
                return <em>{t('export_all_data_sources')}</em>;
              }
              const selectedNames = selected.map(selectedObject => selectedObject.displayName);
              return selectedNames.join(', '); // Render the names of all selected classes, seperated by comma
            }}
          >
            <MenuItem value={'all'}>
              <ListItemText>
                {selectedDataSources.length === dataSources.length
                  ? t('export_deselect_all')
                  : t('export_select_all')}
              </ListItemText>
            </MenuItem>
            {dataSources.map(dataSource => (
              <MenuItem
                key={dataSource.id}
                value={{ id: dataSource.id, displayName: dataSource.name }}
              >
                <Checkbox
                  checked={selectedDataSources.some(item => item.id === dataSource.id)}
                  sx={{
                    color: ColorsV2.acent,
                    '&.Mui-checked': {
                      color: ColorsV2.acent,
                    },
                    '&.MuiCheckbox-root': {},
                  }}
                />
                <ListItemText>{dataSource.name}</ListItemText>
              </MenuItem>
            ))}
          </Select>
        ) : (
          <></>
        )}
        {informationClasses.length > 0 ? (
          <Tooltip
            open={!informationClassOpen && informationClassHover}
            disableHoverListener={informationClassOpen}
            title={t('export_information_select_warning_tooltip')}
          >
            <Stack
              onMouseEnter={() => setInformationClassHover(true)}
              onMouseLeave={() => setInformationClassHover(false)}
            >
              <Select
                multiple
                displayEmpty
                name='information-classes'
                open={informationClassOpen}
                onOpen={() => {
                  setInformationClassOpen(true);
                  setInformationClassHover(false);
                }}
                onClose={() => {
                  setInformationClassOpen(false);
                  setInformationClassHover(false);
                }}
                onChange={handleChange}
                value={selectedInformationClasses}
                input={<StyledInput />}
                MenuProps={menuProps}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    // Disable default focus styling
                    borderColor: 'transparent',
                  },
                  '&.MuiOutlinedInput-root.Mui-focused': {
                    boxShadow: 'none',
                  },
                }}
                renderValue={selected => {
                  if (selected.length === 0) {
                    return <em>{t('export_select_information_classes')}</em>; // Placeholder text
                  }
                  if (selected.length === informationClasses.length) {
                    return <em>{t('export_all_information_classes')}</em>;
                  }
                  const selectedNames = selected.map(selectedObject => selectedObject.displayName);
                  return selectedNames.join(', '); // Render the names of all selected classes, seperated by comma
                }}
              >
                <MenuItem value={'all'}>
                  <ListItemText>
                    {selectedInformationClasses.length === informationClasses.length
                      ? t('export_deselect_all')
                      : t('export_select_all')}
                  </ListItemText>
                </MenuItem>
                {informationClasses.map(informationClass => (
                  <MenuItem
                    key={informationClass.id}
                    value={{ id: informationClass.id, displayName: informationClass.name }}
                  >
                    <Checkbox
                      checked={selectedInformationClasses.some(
                        item => item.id === informationClass.id
                      )}
                      sx={{
                        color: ColorsV2.acent,
                        '&.Mui-checked': {
                          color: ColorsV2.acent,
                        },
                        '&.MuiCheckbox-root': {},
                      }}
                    />
                    <ListItemText>{informationClass.name}</ListItemText>
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Tooltip>
        ) : (
          <></>
        )}
        {selectedInformationClasses.length > 0 ? (
          <Select
            multiple
            displayEmpty
            name='information-types'
            onChange={handleChange}
            value={selectedInformationTypes}
            input={<StyledInput />}
            MenuProps={menuProps}
            sx={{
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                // Disable default focus styling
                borderColor: 'transparent',
              },
              '&.MuiOutlinedInput-root.Mui-focused': {
                boxShadow: 'none',
              },
            }}
            renderValue={selected => {
              if (selected.length === 0) {
                return <em>{t('export_select_information_types')}</em>; // Placeholder text
              }
              if (selected.length === informationTypes.length) {
                return <em>{t('export_all_information_types')}</em>;
              }
              const selectedNames = selected.map(selectedObject => selectedObject.displayName);
              return selectedNames.join(', '); // Render the names of all selected types, seperated by comma
            }}
          >
            <MenuItem value={'all'}>
              <ListItemText>
                {selectedInformationTypes.length === informationTypes.length
                  ? t('export_deselect_all')
                  : t('export_select_all')}
              </ListItemText>
            </MenuItem>
            {informationTypes.map(informationType => (
              <MenuItem
                key={informationType.id}
                value={{ id: informationType.id, displayName: informationType.name }}
              >
                <Checkbox
                  checked={selectedInformationTypes.some(item => item.id === informationType.id)}
                  sx={{
                    color: ColorsV2.acent,
                    '&.Mui-checked': {
                      color: ColorsV2.acent,
                    },
                    '&.MuiCheckbox-root': {},
                  }}
                />
                <ListItemText>{informationType.name}</ListItemText>
              </MenuItem>
            ))}
          </Select>
        ) : (
          <></>
        )}
        {selectedInformationClasses.length > 0 ? (
          <Select
            multiple
            displayEmpty
            name='restrictions'
            onChange={handleChange}
            value={selectedRestrictions}
            input={<StyledInput />}
            MenuProps={menuProps}
            sx={{
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                // Disable default focus styling
                borderColor: 'transparent',
              },
              '&.MuiOutlinedInput-root.Mui-focused': {
                boxShadow: 'none',
              },
            }}
            renderValue={selected => {
              if (selected.length === 0) {
                return <em>{t('export_select_restrictions')}</em>; // Placeholder text
              }
              if (selected.length === restrictions.length) {
                return <em>{t('export_all_restrictions')}</em>;
              }
              const selectedNames = selected.map(selectedObject => selectedObject.displayName);
              return selectedNames.join(', '); // Render the names of all selected restrictions, seperated by comma
            }}
          >
            <MenuItem value={'all'}>
              <ListItemText>
                {selectedRestrictions.length === restrictions.length
                  ? t('export_deselect_all')
                  : t('export_select_all')}
              </ListItemText>
            </MenuItem>
            {restrictions.map(restriction => (
              <MenuItem
                key={restriction.id}
                value={{ id: restriction.id, displayName: restriction.name }}
              >
                <Checkbox
                  checked={selectedRestrictions.some(item => item.id === restriction.id)}
                  sx={{
                    color: ColorsV2.acent,
                    '&.Mui-checked': {
                      color: ColorsV2.acent,
                    },
                    '&.MuiCheckbox-root': {},
                  }}
                />
                <ListItemText>{restriction.name}</ListItemText>
              </MenuItem>
            ))}
          </Select>
        ) : (
          <></>
        )}
      </Stack>
      <Stack direction='row' justifyContent='flex-end' alignItems='center' sx={{ width: '100%' }}>
        <Typography
          variant='subtitle1'
          sx={{
            color: ColorsV2.textInfo,
            padding: '10px',
          }}
        >
          {selectedDataSources.length > 0 &&
          selectedInformationClasses.length > 0 &&
          selectedInformationTypes.length > 0 &&
          selectedRestrictions.length > 0
            ? t('export_estimated_deviations') + estimatedDeviations
            : t('export_estimated_deviations_placeholder')}
        </Typography>
        <Button
          onClick={generateReport}
          variant='contained'
          startIcon={<DownloadingOutlinedIcon />}
          disabled={
            !(
              selectedDataSources.length > 0 &&
              selectedInformationClasses.length > 0 &&
              selectedInformationTypes.length > 0 &&
              selectedRestrictions.length > 0
            )
          }
          sx={{
            ...Buttons.baseStyle.primaryButtonHighPriority,
            color: ColorsV2.white,
            backgroundColor: ColorsV2.acent,
            marginRight: '20px',
            marginBottom: '10px',
            marginTop: '10px',
          }}
        >
          {t('export_button')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ViolationExportMenu;
